import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as ContextMenu from '@radix-ui/react-context-menu';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Pressable, View } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Task } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useGlobalPointerListener } from '@hooks/useOutsideClick.web';
import { useSelectableListWeb } from '@hooks/useSelectableListWeb';
import { getTaskMembership } from '@hooks/useTaskMembership';
import Images from '@themes/images';
import theme from '@themes/theme';

export type SelectableDndKitListProps<T extends { id: string }> = {
  data: T[];
  renderItem: (
    item: T,
    isLast: boolean,
    isSelected: boolean
  ) => React.ReactNode;

  onOrderChange?: (
    newData: T[],
    meta: {
      movedIds: string[];
      afterId: string | null;
      beforeId: string | null;
    }
  ) => void;

  onRightMenuAction: (
    actionType:
      | 'complete'
      | 'assignee'
      | 'duplicate'
      | 'moveUp'
      | 'moveDown'
      | 'delete',
    selectedData: T[]
  ) => void | Promise<void>;

  // "Infinite scroll / load more"
  onLoadMore?: () => void | Promise<void>;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreThresholdPx?: number;
  enableRightClickMenu?: boolean;
  draggable?: boolean;
};

type InsertIndicator = { type: 'top' | 'bottom'; rowIndex: number } | null;

type DragBadge = {
  count: number;
  label: string;
};

const ROW_PLACEHOLDER_HEIGHT = 56;
const DRAG_OVERLAY_SHADOW = 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(n, max));
}

/**
 * "Move a block of ids to a boundary index in the full list space."
 * boundary: 0..len (insert before first .. after last)
 */
function moveBlock<T extends { id: string }>(
  list: T[],
  dragGroupIds: string[],
  boundary: number
): T[] {
  const selectedSet = new Set(dragGroupIds);
  const group = list.filter((it) => selectedSet.has(it.id));
  const base = list.filter((it) => !selectedSet.has(it.id));

  // "Convert boundary in full-list space -> insertion index in base space"
  let baseInsertIndex = 0;
  for (let i = 0; i < boundary; i += 1) {
    const it = list[i];
    if (it && !selectedSet.has(it.id)) baseInsertIndex += 1;
  }
  baseInsertIndex = clamp(baseInsertIndex, 0, base.length);

  return [
    ...base.slice(0, baseInsertIndex),
    ...group,
    ...base.slice(baseInsertIndex),
  ];
}

/**
 * "Radix Context Menu item wrapper."
 * - "Do NOT call event.preventDefault() in onSelect, otherwise Radix keeps the menu open."
 */
const RadixMenuItem = ({
  onSelect,
  iconName,
  iconColor,
  title,
  titleColor,
  disabled = false,
  hasBottomBorder = false,
}: {
  onSelect: () => void | Promise<void>;
  iconName: keyof typeof Images;
  iconColor: keyof typeof theme.colors;
  title: string;
  titleColor: keyof typeof theme.colors;
  disabled?: boolean;
  hasBottomBorder?: boolean;
}) => {
  return (
    <ContextMenu.Item
      disabled={disabled}
      onSelect={() => onSelect()}
      data-radix-context-menu-item
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
      }}>
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 16,
          paddingRight: 16,
          borderBottom: hasBottomBorder
            ? `1px solid ${theme.colors.grey02}`
            : '1px solid transparent',
        }}>
        <Icon
          name={iconName}
          color={disabled ? 'grey04' : iconColor}
          variant='s'
        />
        <Text
          ml='m'
          variant='webBodySecondary'
          color={disabled ? 'grey04' : titleColor}>
          {title}
        </Text>
      </div>
    </ContextMenu.Item>
  );
};

/**
 * "A single sortable row."
 * Performance:
 * - "Memoized to reduce re-render cost for hundreds of rows."
 * - "Disable text selection to avoid browser highlighting on click/drag."
 */
const SortableRowBase = <T extends { id: string }>(props: {
  item: T;
  index: number;
  isLast: boolean;
  isSelected: boolean;
  isInDragGroup: boolean;
  isActiveDragLeader: boolean;
  renderItem: SelectableDndKitListProps<T>['renderItem'];
  onPressRow: (e: any, id: string) => void;
  setOverHalf: (id: string, half: 'top' | 'bottom') => void;
  activeId: string | null;
  draggable: boolean;
}) => {
  const {
    item,
    isLast,
    isSelected,
    isInDragGroup,
    isActiveDragLeader,
    renderItem,
    onPressRow,
    setOverHalf,
    activeId,
    draggable,
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id, disabled: !draggable });

  // "Jira/Notion style: do not animate/reflow rows while dragging. Only DragOverlay moves."
  const shouldDisableTransform = !!activeId;

  const style: any = {
    transform: shouldDisableTransform
      ? undefined
      : CSS.Transform.toString(transform),
    transition: shouldDisableTransform ? undefined : transition,
    position: 'relative',
    zIndex: 1,
  };

  // "Track pointer top/bottom half for insert indicator boundary calculation."
  const onPointerMove = (e: any) => {
    if (!activeId) return;
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const y = e.clientY ?? e.nativeEvent?.clientY;
    if (typeof y !== 'number') return;
    const half = y < rect.top + rect.height / 2 ? 'top' : 'bottom';
    setOverHalf(item.id, half);
  };

  // "Active leader placeholder"
  if (activeId && isActiveDragLeader) {
    return (
      <div
        ref={setNodeRef as any}
        style={{
          ...style,
          height: ROW_PLACEHOLDER_HEIGHT,
          background: theme.colors.grey02,
        }}
      />
    );
  }

  // "Multi-select drag placeholders"
  if (activeId && isInDragGroup) {
    return (
      <div
        ref={setNodeRef as any}
        style={{
          ...style,
          height: ROW_PLACEHOLDER_HEIGHT,
          background: theme.colors.grey02,
        }}
      />
    );
  }

  return (
    <div
      ref={setNodeRef as any}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      style={{
        ...style,
        overflow: 'visible',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
      onPointerMove={draggable ? onPointerMove : undefined}>
      <Pressable
        dataSet={{ selectableRow: '1', rowId: item.id }}
        onPress={(e) => onPressRow(e, item.id)}
        style={[
          styles.row,
          isSelected && styles.rowSelected,
          { outlineStyle: 'none', outlineWidth: 0, boxShadow: 'none' } as any,
        ]}>
        <Box style={{ flex: 1 }}>{renderItem(item, isLast, isSelected)}</Box>
      </Pressable>
    </div>
  );
};

const SortableRow = React.memo(SortableRowBase) as typeof SortableRowBase;

export const SelectableDndKitListWeb = <T extends { id: string }>({
  data,
  renderItem,
  onOrderChange,
  onRightMenuAction,
  isLoadingMore,
  onLoadMore,
  hasMore,
  loadMoreThresholdPx = 25,
  draggable = false,
  enableRightClickMenu = true,
}: SelectableDndKitListProps<T>) => {
  const innerData = data;

  const {
    selectedIds,
    isSelected,
    handleItemPress,
    clearSelection,
    setSelectedIds,
  } = useSelectableListWeb();

  // ---------------------------
  // "Inject minimal CSS for hiding scrollbar + Radix highlight styles"
  // ---------------------------
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const hideScrollbarId = 'selectable-dnd-list-hide-scrollbar';
    if (!document.getElementById(hideScrollbarId)) {
      const style = document.createElement('style');
      style.id = hideScrollbarId;
      style.innerHTML = `
        [data-scroll-container="selectable-dnd-list"]::-webkit-scrollbar {
          width: 0px;
          height: 0px;
          display: none;
        }
      `;
      document.head.appendChild(style);
    }

    const radixStylesId = 'radix-context-menu-styles';
    if (!document.getElementById(radixStylesId)) {
      const style = document.createElement('style');
      style.id = radixStylesId;
      style.innerHTML = `
        [data-radix-context-menu-content] {
          border-radius: 8px;
          overflow: hidden;
        }

        [data-radix-context-menu-item][data-highlighted] {
          background: ${theme.colors.grey01};
          outline: none;
        }

        [data-radix-context-menu-item][data-disabled] {
          opacity: 0.5;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ---------------------------
  // "Single (global) ContextMenu state"
  // ---------------------------
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTargetIds, setMenuTargetIds] = useState<string[]>([]);
  const [contextFocusId, setContextFocusId] = useState<string | null>(null);
  const menuContentRef = useRef<HTMLDivElement | null>(null);

  // "Avoid allocating objects on every render; keep stable refs for menu state"
  const menuOpenRef = useRef(false);
  useEffect(() => {
    menuOpenRef.current = menuOpen;
  }, [menuOpen]);

  // "Prevent immediate reopen after scroll-close (race with Radix open events)"
  const suppressReopenUntilRef = useRef(0);
  const suppressReopenForMs = (ms: number) => {
    suppressReopenUntilRef.current = Date.now() + ms;
  };
  const isReopenSuppressed = () => Date.now() < suppressReopenUntilRef.current;

  const closeMenu = useCallback(
    (reason: 'scroll' | 'click' | 'escape' | 'drag' = 'click') => {
      if (reason === 'scroll') suppressReopenForMs(250);

      startTransition(() => {
        setMenuOpen(false);
        setMenuTargetIds([]);
        setContextFocusId(null);
      });
    },
    []
  );

  // "Bind global scroll/wheel listeners ONLY when menu is open"
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!menuOpen) return;

    const handler = () => closeMenu('scroll');

    document.addEventListener('wheel', handler, {
      capture: true,
      passive: true,
    });
    document.addEventListener('scroll', handler, { capture: true });
    document.addEventListener('touchmove', handler, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener('wheel', handler as any, true as any);
      document.removeEventListener('scroll', handler as any, true as any);
      document.removeEventListener('touchmove', handler as any, true as any);
    };
  }, [menuOpen, closeMenu]);

  // "Fallback outside-click close"
  const handleFallbackOutsideClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuContentRef.current?.contains(target)) return;
      closeMenu('click');
    },
    [closeMenu]
  );

  useGlobalPointerListener('mousedown', handleFallbackOutsideClick, {
    enabled: menuOpen,
  });

  // "Clear row selection when clicking outside the selectable list."
  const handleClearSelectionOutsideClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      if (menuContentRef.current?.contains(target)) return;

      setSelectedIds((prev) => (prev.size > 0 ? new Set() : prev));
      setMenuTargetIds((prev) => (prev.length > 0 ? [] : prev));
      setContextFocusId((prev) => (prev === null ? prev : null));
      lastAnchorIdRef.current = null;
    },
    [setSelectedIds]
  );

  useGlobalPointerListener('mousedown', handleClearSelectionOutsideClick);

  // ---------------------------
  // "Right click selection"
  // ---------------------------
  const getMods = (e: any) => {
    const ne = e?.nativeEvent ?? e;
    return {
      shift: !!ne?.shiftKey,
      ctrl: !!ne?.ctrlKey,
      meta: !!ne?.metaKey,
    };
  };

  const lastAnchorIdRef = useRef<string | null>(null);

  const prepareMenuTargetIds = useCallback(
    (e: any, id: string) => {
      const { shift, ctrl, meta } = getMods(e);
      const hasKeepModifier = ctrl || meta;
      const hasAnyModifier = shift || ctrl || meta;

      // "If row already selected -> keep current selection"
      if (selectedIds.has(id)) {
        const ids = Array.from(selectedIds);
        setMenuTargetIds(ids);
        setContextFocusId(null);
        lastAnchorIdRef.current = id;
        return;
      }

      // "If no modifiers -> clear previous selection and select only this row"
      if (!hasAnyModifier) {
        setSelectedIds(new Set([id]));
        setMenuTargetIds([id]);
        setContextFocusId(null);
        lastAnchorIdRef.current = id;
        return;
      }

      // "Ctrl/Meta right click -> add to selection"
      if (hasKeepModifier) {
        setSelectedIds((prev: Set<string>) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });

        // "Use current selectedIds + id for menu immediately"
        const nextIds = new Set(selectedIds);
        nextIds.add(id);
        setMenuTargetIds(Array.from(nextIds));

        setContextFocusId(null);
        lastAnchorIdRef.current = id;
        return;
      }

      // "Shift right click -> treat as single select (simple and predictable)"
      setSelectedIds(new Set([id]));
      setMenuTargetIds([id]);
      setContextFocusId(null);
      lastAnchorIdRef.current = id;
    },
    [selectedIds, setSelectedIds]
  );

  const openMenuForRow = useCallback(
    (e: any, id: string) => {
      if (isReopenSuppressed()) return;

      // "Update selection first, then open menu"
      prepareMenuTargetIds(e, id);
      setMenuOpen(true);
    },
    [prepareMenuTargetIds]
  );

  const getSelectedIdsForMenu = useCallback(() => {
    const ids = menuTargetIds.length ? menuTargetIds : Array.from(selectedIds);
    return new Set(ids);
  }, [menuTargetIds, selectedIds]);

  const getSelectedDataForMenu = useCallback(() => {
    const idSet = getSelectedIdsForMenu();
    return data.filter((t) => idSet.has(t.id));
  }, [data, getSelectedIdsForMenu]);

  // ---------------------------
  // "Infinite scroll / load more"
  // ---------------------------
  const containerRef = useRef<HTMLElement | null>(null);
  const userScrolledRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const lastTriggerAtRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const thresholdPx = loadMoreThresholdPx ?? 160;
  const hasMoreSafe = hasMore ?? true;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const portalDivRef = useRef<HTMLDivElement | null>(null);
  const [dragItemWidth, setDragItemWidth] = useState<number>(0);

  const maybeLoadMore = useCallback(async () => {
    if (!onLoadMore) return;
    if (!hasMoreSafe) return;
    if (isLoadingMore) return;
    if (loadingMoreRef.current) return;
    if (!userScrolledRef.current) return;

    // "Avoid loading while dragging or menu is open"
    if (activeId) return;
    if (menuOpenRef.current) return;

    const el = containerRef.current as any;
    if (!el) return;

    const scrollTop = el.scrollTop ?? 0;
    const clientHeight = el.clientHeight ?? 0;
    const scrollHeight = el.scrollHeight ?? 0;

    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
    if (distanceToBottom > thresholdPx) return;

    const now = Date.now();
    if (now - lastTriggerAtRef.current < 400) return;
    lastTriggerAtRef.current = now;

    try {
      loadingMoreRef.current = true;
      await onLoadMore();
    } finally {
      loadingMoreRef.current = false;
    }
  }, [onLoadMore, hasMoreSafe, isLoadingMore, thresholdPx, activeId]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  const prevLenRef = useRef(innerData.length);
  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = innerData.length;
    if (innerData.length > prev) maybeLoadMore();
  }, [innerData.length, maybeLoadMore]);

  const onScrollContainer = useCallback(() => {
    userScrolledRef.current = true;

    // "Close menu on scroll, but do NOT do extra work when already closed"
    if (menuOpenRef.current) closeMenu('scroll');

    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      maybeLoadMore();
    });
  }, [maybeLoadMore, closeMenu]);

  // ---------------------------
  // "Selection helpers"
  // ---------------------------
  const getEventKey = (e: any, key: 'shiftKey' | 'metaKey' | 'ctrlKey') => {
    return !!(e?.shiftKey ?? e?.nativeEvent?.[key]);
  };

  const selectRange = useCallback(
    (fromId: string, toId: string, keepExisting: boolean) => {
      const ids = innerData.map((it) => it.id);
      const a = ids.indexOf(fromId);
      const b = ids.indexOf(toId);
      if (a < 0 || b < 0) return;

      const [start, end] = a <= b ? [a, b] : [b, a];
      const rangeIds = ids.slice(start, end + 1);

      setSelectedIds((prev: Set<string>) => {
        const next = keepExisting ? new Set(prev) : new Set<string>();
        rangeIds.forEach((rid) => next.add(rid));
        return next;
      });
    },
    [innerData, setSelectedIds]
  );

  const skipNextPressRef = useRef(false);

  const handleRowSelect = useCallback(
    (e: any, id: string) => {
      if (activeId) return;

      const isShift = getEventKey(e, 'shiftKey');
      const isMeta = getEventKey(e, 'metaKey');
      const isCtrl = getEventKey(e, 'ctrlKey');
      const keepExisting = isMeta || isCtrl;

      if (!lastAnchorIdRef.current) lastAnchorIdRef.current = id;

      if (isShift && lastAnchorIdRef.current) {
        selectRange(lastAnchorIdRef.current, id, keepExisting);
      } else {
        handleItemPress(e, id);
        lastAnchorIdRef.current = id;
      }
    },
    [activeId, handleItemPress, selectRange]
  );

  // ---------------------------
  // "dnd-kit setup"
  // ---------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const dragGroupIdsRef = useRef<string[]>([]);
  const [overId, setOverId] = useState<string | null>(null);
  const overHalfRef = useRef<Record<string, 'top' | 'bottom'>>({});

  const setOverHalf = (id: string, half: 'top' | 'bottom') => {
    overHalfRef.current[id] = half;
  };

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return innerData.find((it) => it.id === activeId) ?? null;
  }, [activeId, innerData]);

  const dragBadge: DragBadge | null = useMemo(() => {
    if (!activeId) return null;
    const count = dragGroupIdsRef.current?.length || 1;
    const label = `${count} ${count > 1 ? 'tasks' : 'task'}`;
    return { count, label };
  }, [activeId]);

  const getIndicator = useCallback((): InsertIndicator => {
    if (!activeId || !overId) return null;

    const len = innerData.length;
    const overIndex = innerData.findIndex((it) => it.id === overId);
    if (overIndex < 0) return null;

    const half = overHalfRef.current[overId] ?? 'top';
    const boundary = clamp(overIndex + (half === 'bottom' ? 1 : 0), 0, len);

    if (boundary <= 0) return { type: 'top', rowIndex: 0 };
    if (boundary >= len) return { type: 'bottom', rowIndex: len - 1 };
    return { type: 'top', rowIndex: boundary };
  }, [activeId, overId, innerData]);

  const indicator = getIndicator();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id);

      if (selectedIds.has(id)) {
        dragGroupIdsRef.current = Array.from(selectedIds);
      } else {
        clearSelection?.();
        setSelectedIds(new Set([id]));
        dragGroupIdsRef.current = [id];
      }

      closeMenu('drag');
      setActiveId(id);

      if (event.active.rect.current.initial) {
        setDragOffset({
          x: event.active.rect.current.initial.left,
          y: event.active.rect.current.initial.top,
        });
        setDragItemWidth(event.active.rect.current.initial.width);
      }
    },
    [selectedIds, clearSelection, setSelectedIds, closeMenu]
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const oid = event.over?.id ? String(event.over.id) : null;
    setOverId(oid);
  }, []);

  const resetDragState = useCallback(() => {
    setActiveId(null);
    setOverId(null);
    setDragOffset(null);
    setDragItemWidth(0);
    overHalfRef.current = {};
    dragGroupIdsRef.current = [];
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      const aId = active?.id ? String(active.id) : null;
      const oId = over?.id ? String(over.id) : null;

      if (!aId || !oId) {
        resetDragState();
        return;
      }

      if (aId === oId) {
        resetDragState();
        return;
      }

      const len = innerData.length;
      const overIndex = innerData.findIndex((it) => it.id === oId);
      if (overIndex < 0) {
        resetDragState();
        return;
      }

      const half = overHalfRef.current[oId] ?? 'top';
      const boundary = clamp(overIndex + (half === 'bottom' ? 1 : 0), 0, len);

      const groupIds = dragGroupIdsRef.current.length
        ? dragGroupIdsRef.current
        : [aId];

      const next = moveBlock(innerData, groupIds, boundary);
      const movedStartIndex = next.findIndex((it) => groupIds.includes(it.id));
      const afterId =
        movedStartIndex > 0 ? (next[movedStartIndex - 1]?.id ?? null) : null;
      const beforeId =
        movedStartIndex >= 0 && movedStartIndex + groupIds.length < next.length
          ? (next[movedStartIndex + groupIds.length]?.id ?? null)
          : null;

      onOrderChange?.(next, {
        movedIds: groupIds,
        afterId,
        beforeId,
      });

      resetDragState();
    },
    [innerData, onOrderChange, resetDragState]
  );

  // ---------------------------
  // "UI helpers"
  // ---------------------------
  // Dot size for the insert position marker — no exact theme.spacing token for 10px
  const INSERT_MARKER_DOT_SIZE = 10;

  const InsertMarker = () => {
    return (
      <div style={{ position: 'relative', pointerEvents: 'none', zIndex: 50 }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: theme.colors.greenSecondary,
            top: -1,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: -(INSERT_MARKER_DOT_SIZE / 2),
            width: INSERT_MARKER_DOT_SIZE,
            height: INSERT_MARKER_DOT_SIZE,
            borderRadius: INSERT_MARKER_DOT_SIZE / 2,
            backgroundColor: theme.colors.greenSecondary,
            // 80 hex ≈ 50% opacity; matches greenSecondary rather than hardcoded rgba
            boxShadow: `0 0 4px ${theme.colors.greenSecondary}80`,
          }}
        />
      </div>
    );
  };

  const OverlayContent = () => {
    if (!activeItem) return null;

    return (
      <div style={{ position: 'relative', zIndex: 999999, opacity: 0.85 }}>
        {dragBadge && dragBadge.count > 1 && (
          <View
            style={{
              position: 'absolute',
              left: 24,
              top: -10,
              width: 22,
              height: 22,
              borderRadius: 11,
              backgroundColor: '#000',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              shadowColor: '#000',
              shadowOpacity: 0.2,
              shadowRadius: 4,
              shadowOffset: { width: 0, height: 2 },
            }}
            pointerEvents='none'>
            <Text
              style={{
                color: '#fff',
                fontSize: 12,
                fontWeight: '600',
                lineHeight: 12,
              }}>
              {dragBadge.count}
            </Text>
          </View>
        )}

        {renderItem(
          activeItem,
          false,
          isSelected(activeItem.id) || contextFocusId === activeItem.id
        )}
      </div>
    );
  };

  // ---------------------------
  // "Permissions (compute only when menu is open)"
  // ---------------------------
  const { me } = useMe();
  const [cannotEdit, setCannotEdit] = useState(false);
  const [cannotAssign, setCannotAssign] = useState(false);
  const [cannotDelete, setCannotDelete] = useState(false);

  const permissionIds = useMemo(() => {
    if (!menuOpen) return new Set<string>();
    return getSelectedIdsForMenu();
  }, [menuOpen, getSelectedIdsForMenu]);

  useEffect(() => {
    if (!menuOpen) return;

    let cannotEdit1 = false;
    let cannotDelete1 = false;

    const list = data.filter((t) => permissionIds.has(t.id));
    if (list.length === 1) {
      const rtn = getTaskMembership(list[0] as Task, me?.id);
      setCannotEdit(!rtn.canEditTaskSecondLevel);
      setCannotAssign(!rtn.canEditTaskSecondLevel);
      setCannotDelete(!rtn.canEditTaskTopLevel);
    } else {
      let topLevelCount = 0;
      let secondLevelCount = 0;

      for (const item of list) {
        const rtn = getTaskMembership(item as Task, me?.id);
        if (!rtn.canEditTaskSecondLevel) {
          cannotEdit1 = true;
          cannotDelete1 = true;
          break;
        } else if (!rtn.canEditTaskTopLevel) {
          cannotDelete1 = true;
          secondLevelCount += 1;
        } else {
          topLevelCount += 1;
        }
      }

      setCannotEdit(cannotEdit1);

      const selectedSize = permissionIds.size;
      if (topLevelCount === selectedSize || secondLevelCount === selectedSize) {
        setCannotAssign(cannotEdit1);
      } else {
        setCannotAssign(true);
      }

      setCannotDelete(cannotDelete1);
    }
  }, [menuOpen, permissionIds, data, me?.id]);

  const isCompleted = useCallback(() => {
    const list = getSelectedDataForMenu();
    return list.length > 0 && (list as any)[0].completedAt !== null;
  }, [getSelectedDataForMenu]);

  // ---------------------------
  // ✅ "Event delegation: attach handlers once on the container"
  // ---------------------------
  const listWrapperRef = useRef<HTMLDivElement | null>(null);

  const findRowIdFromEvent = useCallback((e: any) => {
    const target = e?.target as HTMLElement | null;
    if (!target) return null;

    // "Find the nearest element marked as a selectable row container"
    const rowEl = target.closest('[data-row-id]') as HTMLElement | null;
    return rowEl?.getAttribute('data-row-id') ?? null;
  }, []);

  const onContextMenuCaptureDelegated = useCallback(
    (e: any) => {
      const rowId = findRowIdFromEvent(e);
      if (!rowId) return;
      openMenuForRow(e, rowId);
    },
    [findRowIdFromEvent, openMenuForRow]
  );

  const onMouseDownCaptureDelegated = useCallback(
    (e: any) => {
      // "Left button only"
      if (e?.button !== 0) return;

      const rowId = findRowIdFromEvent(e);
      if (!rowId) return;

      // "Shift click: stop propagation to keep selection consistent"
      if (e.shiftKey) e.stopPropagation();

      skipNextPressRef.current = true;
      handleRowSelect(e, rowId);
    },
    [findRowIdFromEvent, handleRowSelect]
  );

  // ---------------------------
  // "Render"
  // ---------------------------
  return (
    <Box
      ref={(node: any) => {
        containerRef.current = node;
      }}
      data-scroll-container='selectable-dnd-list'
      style={
        {
          minHeight: 0,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        } as any
      }
      onScroll={onScrollContainer}>
      <DndContext
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragStart={draggable ? handleDragStart : undefined}
        onDragMove={
          draggable
            ? (e: DragMoveEvent) => {
                const translated = e.active.rect.current.translated;
                if (!translated) return;
                const x = translated.left;
                const y = translated.top;
                if (portalDivRef.current) {
                  // Portal already mounted — update transform imperatively (no re-render)
                  portalDivRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
                } else {
                  // Portal not yet mounted — one-time state set to trigger mount
                  setDragOffset({ x, y });
                }
              }
            : undefined
        }
        onDragOver={draggable ? handleDragOver : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
        onDragCancel={draggable ? resetDragState : undefined}>
        <SortableContext
          items={innerData.map((it) => it.id)}
          strategy={verticalListSortingStrategy}>
          <ContextMenu.Root
            open={menuOpen}
            onOpenChange={(open) => {
              if (!open) closeMenu('click');
              else setMenuOpen(true);
            }}>
            <ContextMenu.Trigger asChild>
              {/* ✅ "Attach handlers only once here (event delegation)" */}
              <div
                ref={(n) => {
                  listWrapperRef.current = n;
                }}
                onContextMenuCapture={onContextMenuCaptureDelegated as any}
                onMouseDownCapture={onMouseDownCaptureDelegated as any}
                style={{ width: '100%' }}>
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                  {innerData.map((item, index) => {
                    const isLast = index === innerData.length - 1;

                    const groupIds = dragGroupIdsRef.current;
                    const isInDragGroup = activeId
                      ? groupIds.includes(item.id)
                      : false;
                    const isActiveDragLeader =
                      !!activeId && item.id === activeId;

                    const showTop =
                      indicator?.type === 'top' && indicator.rowIndex === index;
                    const showBottom =
                      indicator?.type === 'bottom' &&
                      indicator.rowIndex === index;

                    const rowSelected =
                      isSelected(item.id) || contextFocusId === item.id;

                    return (
                      <React.Fragment key={item.id}>
                        {showTop && <InsertMarker />}
                        <Box style={{ overflow: 'visible' }}>
                          {/* ✅ "Mark row container with data-row-id for event delegation" */}
                          <div data-row-id={item.id} style={{ width: '100%' }}>
                            <SortableRow<T>
                              item={item}
                              index={index}
                              isLast={isLast}
                              isSelected={rowSelected}
                              isInDragGroup={isInDragGroup}
                              isActiveDragLeader={isActiveDragLeader}
                              renderItem={renderItem}
                              draggable={draggable}
                              onPressRow={(e, id) => {
                                if (skipNextPressRef.current) {
                                  skipNextPressRef.current = false;
                                  return;
                                }
                                handleRowSelect(e, id);
                              }}
                              setOverHalf={setOverHalf}
                              activeId={activeId}
                            />
                          </div>
                        </Box>
                        {showBottom && <InsertMarker />}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </div>
            </ContextMenu.Trigger>

            {menuOpen && enableRightClickMenu && (
              <ContextMenu.Portal>
                <ContextMenu.Content
                  ref={(node) => {
                    menuContentRef.current = node as any;
                  }}
                  data-radix-context-menu-content
                  sideOffset={6}
                  align='start'
                  collisionPadding={8}
                  onEscapeKeyDown={() => closeMenu('escape')}
                  onPointerDownOutside={() => closeMenu('click')}
                  onInteractOutside={() => closeMenu('click')}
                  onWheelCapture={() => closeMenu('scroll')}
                  onTouchMoveCapture={() => closeMenu('scroll')}
                  style={{
                    width: 200,
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.grey02}`,
                    borderRadius: 8,
                    paddingTop: 6,
                    paddingBottom: 6,
                    boxShadow: '0px 6px 16px rgba(0,0,0,0.12)',
                    zIndex: 2147483647,
                  }}>
                  {!isCompleted() && (
                    <RadixMenuItem
                      disabled={cannotEdit}
                      iconName='CheckCircle'
                      iconColor='greenSecondary'
                      title='Mark as Complete'
                      titleColor='greenSecondary'
                      onSelect={() => {
                        closeMenu();
                        onRightMenuAction('complete', getSelectedDataForMenu());
                      }}
                    />
                  )}

                  {isCompleted() && (
                    <RadixMenuItem
                      disabled={cannotEdit}
                      iconName='crossCircle3'
                      iconColor='grey05'
                      title='Mark as Incomplete'
                      titleColor='grey05'
                      onSelect={() => {
                        closeMenu();
                        onRightMenuAction('complete', getSelectedDataForMenu());
                      }}
                    />
                  )}

                  <RadixMenuItem
                    disabled={cannotAssign}
                    iconName='UserPlus'
                    iconColor='grey05'
                    title='Assignee'
                    titleColor='textPrimary'
                    onSelect={() => {
                      closeMenu();
                      onRightMenuAction('assignee', getSelectedDataForMenu());
                    }}
                  />

                  {(() => {
                    const size = getSelectedIdsForMenu().size;
                    return (
                      <RadixMenuItem
                        disabled={size > 1 || cannotEdit}
                        iconName='Copy'
                        iconColor='grey05'
                        title='Duplicate Task'
                        titleColor='textPrimary'
                        hasBottomBorder
                        onSelect={() => {
                          closeMenu();
                          onRightMenuAction(
                            'duplicate',
                            getSelectedDataForMenu()
                          );
                        }}
                      />
                    );
                  })()}

                  {(() => {
                    const size = getSelectedIdsForMenu().size;
                    return (
                      <RadixMenuItem
                        disabled={cannotDelete}
                        iconName='Trash2'
                        iconColor='alertRed'
                        title={`Delete Task${size > 1 ? 's' : ''}`}
                        titleColor='alertRed'
                        onSelect={() => {
                          closeMenu();
                          onRightMenuAction('delete', getSelectedDataForMenu());
                        }}
                      />
                    );
                  })()}
                </ContextMenu.Content>
              </ContextMenu.Portal>
            )}
          </ContextMenu.Root>
        </SortableContext>

        {activeItem &&
          dragOffset !== null &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={portalDivRef}
              style={{
                position: 'fixed',
                zIndex: 999999,
                pointerEvents: 'none',
                left: 0,
                top: 0,
                width: dragItemWidth || undefined,
                transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)`,
                filter: DRAG_OVERLAY_SHADOW,
              }}>
              <OverlayContent />
            </div>,
            document.body
          )}
      </DndContext>
    </Box>
  );
};

const styles = {
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minHeight: ROW_PLACEHOLDER_HEIGHT,
    flexShrink: 0,
  } as any,
  rowSelected: {
    backgroundColor: '#e6f0ff',
  } as any,
};
